import { FILTERABLE_BUYBOX_KEYS } from '@/features/buybox/composables/useBuyboxData';
import { useLanguageStore } from '@/helpers/composables/useStores';
import { TitleDetail } from '@/interfaces/title-details-graphql';
import { MaybeRefOrGetter, injectLocal, toValue } from '@vueuse/core';
import { ComputedRef, Ref, computed, ref } from 'vue';
import { ClickoutUrl } from '@/helpers/clickout-helper';
import { MonetizationType, PresentationType } from '@/@types/graphql-types';

const internationalOfferTagsByCountry = {
	// CA: 'amz-ca-ft-no-20',
	// AU: 'amz-au-ft-no-22',
	US: 'amz-us-ft-no-20',
	DE: 'amz-de-ft-no-21',
	GB: 'amz-gb-ft-no-21',
	ES: 'amz-es-ft-no-21',
	IT: 'amz-it-ft-no-21',
	FR: 'amz-fr-ft-no-21',
} as Record<string, string>;

export const useInternationalOffersStatus = ({
	loading = ref(false),
	title,
}: {
	loading?: Ref<boolean>;
	title: MaybeRefOrGetter<TitleDetail | undefined>;
}) => {
	const { country } = useLanguageStore();
	const _title = computed(() => toValue(title));

	const hasDomesticOffers = computed(() => {
		// rewritten to not use monetization typed offers instead of `title.offers`
		return FILTERABLE_BUYBOX_KEYS.some(key => {
			return (_title.value?.[key] || []).some(offer => offer.country === country.value);
		});
	});
	const hasInternationalOffers = computed(() => {
		return FILTERABLE_BUYBOX_KEYS.some(key => {
			return (_title.value?.[key] || []).some(
				offer =>
					offer.country !== country.value &&
					offer.monetizationType !== MonetizationType.Cinema &&
					![PresentationType.Bluray, PresentationType.Dvd, PresentationType.Bluray_4K].includes(
						offer.presentationType
					)
			);
		});
	});

	const isActive = computed(
		() => !loading.value && !hasDomesticOffers.value && _title.value?.content?.upcomingReleases?.length === 0
	);

	const isInternationalOffersTitle = computed(() => isActive.value && hasInternationalOffers.value);

	function setNoOfferTag(url: ClickoutUrl) {
		// only set no offer tag, if
		// - current country is in the internationalOfferTagsByCountry
		// - has NO domestic offers
		// - has NO upcoming releases
		if (Object.keys(internationalOfferTagsByCountry).includes(country.value)) {
			if (!hasDomesticOffers.value && _title.value?.content.upcomingReleases.length === 0) {
				return url.set('uct_ctag', internationalOfferTagsByCountry[country.value]);
			}
		}
		return url;
	}

	return {
		isActive,
		hasDomesticOffers, // sometimes we care to know if the page has or hasn't any offers, unrelated to international offers
		hasInternationalOffers,
		setNoOfferTag,
		isInternationalOffersTitle,
	};
};

export const useInternationalOffers = () => {
	const rowCountry = injectLocal<ComputedRef<string | null>>(
		'row-country',
		computed(() => null)
	);

	return {
		rowCountry,
	};
};
